/**
 * useExecuteProposal — DAO-FOUND-03 (Phase 3)
 *
 * Executes a DAO proposal once quorum (≥15 signatures) is reached.
 * Mutation: DAO.executeTransaction(index) + POST /dao/{index}/execute
 * Cache invalidation: list FIRST, then detail (per design §8 invariant).
 * Backend execute record failure is non-fatal because chain state is authoritative.
 *
 * Error handling: handleContractError — no "unknown error" fallback.
 * No query cache override (feedback_query_staletime).
 */

import { useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { toast } from "sonner";
import { useWriteContract } from "wagmi";
import { EXPECTED_CHAIN_ID } from "@/configs/expected-chain";
import { DAO_ABI } from "@/contract/abi/dao";
import { ContractAddress } from "@/contract/addresses";
import { useContractErrorHandler } from "@/hooks/useContractErrorHandler";
import { useRouter } from "@/i18n/navigation";
import { fetchClient } from "@/lib/api-client";

export function useExecuteProposal() {
  const t = useTranslations("dao");
  const router = useRouter();
  const queryClient = useQueryClient();
  const { writeContractAsync, isPending } = useWriteContract();
  const { handleContractError } = useContractErrorHandler();
  const [failedBackendIndex, setFailedBackendIndex] = useState<number | null>(
    null,
  );

  const invalidateProposalQueries = async (index: number) => {
    await queryClient.invalidateQueries({ queryKey: ["get", "/dao"] });
    await queryClient.invalidateQueries({
      queryKey: ["get", `/dao/${index}`],
    });
  };

  const recordBackendExecution = async (index: number): Promise<boolean> => {
    const { error } = await fetchClient.POST("/dao/{index}/execute", {
      params: { path: { index } },
    });

    if (error) {
      setFailedBackendIndex(index);
      toast.error(t("proposals.toasts.executeChainOkRecordFail"));
      return false;
    }

    setFailedBackendIndex(null);
    return true;
  };

  const mutate = async (index: number) => {
    try {
      await writeContractAsync({
        abi: DAO_ABI,
        address: ContractAddress.DAO,
        chainId: EXPECTED_CHAIN_ID,
        functionName: "executeTransaction",
        args: [BigInt(index)],
      });
      const backendRecorded = await recordBackendExecution(index);
      // Invalidate list FIRST, then detail (design §8 mutation success order)
      await invalidateProposalQueries(index);
      if (!backendRecorded) {
        return;
      }
      toast.success(t("proposals.toasts.executeSuccess"));
      router.push("/info/dao/proposals");
    } catch (error) {
      handleContractError(error);
    }
  };

  const retryBackendRecord = async (index?: number) => {
    const targetIndex = index ?? failedBackendIndex;
    if (targetIndex === null) {
      return;
    }

    const backendRecorded = await recordBackendExecution(targetIndex);
    await invalidateProposalQueries(targetIndex);
    if (!backendRecorded) {
      return;
    }

    toast.success(t("proposals.toasts.recordSuccess"));
    router.push("/info/dao/proposals");
  };

  return {
    mutate,
    retryBackendRecord,
    hasBackendRecordFailure: failedBackendIndex !== null,
    isPending,
  };
}
